// Dialer start screen filters. TS mirror of private.queue_filter_options_at
// (supabase/schemas/38_gavion_dialer_filters.sql): the queue rules come from previewQueue, never re-stated here.
import type { CallHoursReport } from "../../reports/types";
import { BUSINESS_TZ } from "../torontoTime";
import { previewQueue, type QueueContext } from "./queueModel";
import type { QueueLead } from "./types";

export type QueueFilterKind = "trade" | "region";
export type QueueFilterOption = {
  kind: QueueFilterKind;
  /** Raw companies.trade / companies.region value (what the session filter sends). */
  value: string;
  /** Leads this rep could be handed right now with this value (session filter ignored). */
  callable_count: number;
};

/** Values kept per kind (SQL: the same cap). */
export const FILTER_OPTIONS_CAP = 200;

const KINDS: readonly QueueFilterKind[] = ["region", "trade"];

/**
 * Every non-blank trade and region of the leads, with how many are callable now (queue eligibility with an empty
 * filter). Values without a callable lead stay listed with 0. Order: kind, count desc, value.
 */
export function queueFilterOptions(
  leads: readonly QueueLead[],
  ctx: QueueContext,
): QueueFilterOption[] {
  const callable = new Set(
    previewQueue(leads, { ...ctx, filter: {} }, Number.MAX_SAFE_INTEGER).map(
      (l) => l.id,
    ),
  );
  return KINDS.flatMap((kind) => {
    const counts = new Map<string, number>();
    for (const l of leads) {
      const v = l[kind];
      if (v == null || !v.trim()) continue;
      counts.set(v, (counts.get(v) ?? 0) + (callable.has(l.id) ? 1 : 0));
    }
    return [...counts]
      .map(([value, callable_count]) => ({ kind, value, callable_count }))
      .sort(
        (a, b) =>
          b.callable_count - a.callable_count ||
          (a.value < b.value ? -1 : a.value > b.value ? 1 : 0),
      )
      .slice(0, FILTER_OPTIONS_CAP);
  });
}

/** ISO weekday (1 = Monday) and hour of an instant in a zone. */
export function isoDowHour(at: Date, tz = BUSINESS_TZ) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "short",
    hour: "numeric",
    hourCycle: "h23",
  }).formatToParts(at);
  const wd = parts.find((p) => p.type === "weekday")?.value ?? "Mon";
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
  const dow = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(wd) + 1;
  return { dow, hour };
}

/**
 * « Meilleur créneau maintenant »: among per-trade call-hours reports, the trade whose cell for the current hour
 * (Toronto weekday × hour; cells are in lead-local time) has the best connect rate with at least the report's
 * window sample (min_sample). Null when no trade has enough data.
 */
export function bestTradeNow(
  entries: readonly { trade: string; report: CallHoursReport }[],
  now: Date,
): string | null {
  const { dow, hour } = isoDowHour(now);
  let best: { trade: string; rate: number; dials: number } | null = null;
  for (const { trade, report } of entries) {
    const c = report.cells.find((x) => x.dow === dow && x.hour === hour);
    if (!c || c.dials <= 0 || c.dials < report.min_sample) continue;
    const rate = c.conversations / c.dials;
    if (
      !best ||
      rate > best.rate ||
      (rate === best.rate && c.dials > best.dials)
    )
      best = { trade, rate, dials: c.dials };
  }
  return best?.trade ?? null;
}
